package com.htmlagent.site.theme

import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px

/**
 * The opaque dark-mode palette of the interface.
 *
 * This is how the application looks, not the criteria the agent generates pages with (that
 * lives in `skills/`). The chrome is deliberately quiet, cool neutrals and a single accent,
 * because the right panel shows pages of arbitrary colors and the interface must not compete
 * with them. The ratios noted on each value are measured over the composited surface where
 * the color is really used, not over the opaque background.
 */
object Palette {

    /** The background. Very deep blue. */
    val Bg = Color.rgb(0x061021)

    /**
     * Opaque cards and panels. The application panels are glass ([Glass]); this is left for
     * whatever really needs to cover what is underneath: a modal, a menu.
     */
    val Surface = Color.rgb(0x111F3A)

    /** What rests on a card, opaque. Same case as [Surface]. */
    val Raised = Color.rgb(0x172B52)

    /** What floats: menus, dialogs. */
    val Floating = Color.rgb(0x18396C)

    /** Primary text. 16.10:1 over the background, generous because sessions are long. */
    val Fg = Color.rgb(0xE7EDF7)

    /**
     * Metadata and hints. Between 4.93:1 and 9.06:1 depending on the area of the glass.
     *
     * Chosen against the lightest area of the translucent panel, where the glow crosses it,
     * which is where it is really read and where a tone tuned to the opaque background fails.
     */
    val Muted = Color.rgb(0xA5B4CE)

    /**
     * The edge of the cards. 1.65:1 is enough: it is decorative, and the 3:1 threshold only
     * applies to non-text elements that carry meaning.
     */
    val Line = Color.rgb(0x18396C)

    /**
     * The reference cobalt, lightened so dark text on top reaches 6.46:1; with the original
     * only large text fit.
     */
    val Accent = Color.rgb(0x5095FF)

    /** An error is never conveyed by color alone: it always comes with text. */
    val Danger = Color.rgb(0xFF9AA8)

    /** While the agent is working. */
    val Working = Color.rgb(0x8FD6B0)

    val Transparent = Colors.Transparent
}

/** The type scale, with a 1.25 ratio from 16px: few steps, well apart. */
object TypeScale {
    val Xs = 0.769.cssRem
    val Sm = 0.877.cssRem
    val Base = 1.cssRem
    val Md = 1.25.cssRem
    val Lg = 1.563.cssRem
    val Xl = 1.953.cssRem

    /**
     * The three steps past the interface, which only the landing uses.
     *
     * They continue the same 1.25 ratio rather than starting a second scale: a display size
     * picked by eye is the one number nobody can defend later.
     */
    val Xxl = 2.441.cssRem

    /** A section headline. */
    val Display = 3.052.cssRem

    /** The one headline on the page that has to be seen before it is read. */
    val Hero = 4.768.cssRem
}

/** Spacing in 4px steps: a single progression for the whole interface. */
object Space {

    /**
     * Half a step. It exists because it was repeating: the padding that only keeps a border
     * off its content, where a whole step would look like a gap.
     */
    val Xxs = 2.px

    val Xs = 4.px
    val Sm = 8.px
    val Md = 12.px
    val Lg = 16.px
    val Xl = 24.px
    val Xxl = 32.px
}

/** Concentric radii: what sits inside a card gets less radius than the card. */
object Radius {

    /** Small controls inside a card. */
    val Sm = 8.px

    /** Fields and buttons. */
    val Md = 12.px

    /** Cards and panels. */
    val Lg = 16.px

    /** A pill. Large enough that the shape comes from the height and not from this number. */
    val Full = 999.px
}

/**
 * The two weights the interface uses.
 *
 * They are here and not written at each call for the same reason as everything else in this
 * file: a third weight appearing somewhere is a decision, and it should have to be made here.
 */
object Weight {

    /** Anything that leads: a heading, a button, a label. */
    const val Semibold = 600

    /** The only place that needs more presence than a heading: the brand. */
    const val Bold = 700


    /**
     * Display only. A headline at this size is a shape before it is a word, and at 700 it
     * reads as a large paragraph instead. It never appears in the application.
     */
    const val Black = 900
}

/**
 * How the interface moves: how long, and with what curve.
 *
 * The agent's own `motion` skill fixes exact durations and curves for the pages it generates;
 * this is the same discipline applied to the tool that generates them. A duration written at the
 * call site is a decision nobody can review, and four of them drift into four different feels.
 */
object Motion {

    /** Something that only confirms a state: an opacity that appears. */
    const val Fast = "120ms"

    /** The usual: a fade that accompanies another change. */
    const val Base = "180ms"

    /** A panel that changes width, where the eye follows the edge. */
    const val Slow = "260ms"

    /** The preview changing size: the longest, because an iframe relays out behind it. */
    const val Slower = "420ms"

    /** The curve for anything that moves in space: fast out, settles without bouncing. */
    const val Curve = "cubic-bezier(0.22, 1, 0.36, 1)"

    /** For what only changes opacity or color, where a curve adds nothing. */
    const val Ease = "ease"

    /** A loop that must not have a beat: the shimmer travelling across a bar. */
    const val Linear = "linear"

    /** A loop that breathes, which is what tells a bar it is still being written. */
    const val Breathe = "ease-in-out"

    /** How long the sheen takes to cross the activity line. */
    const val SheenCycle = "1.8s"

    /** The breathing of a bar being written, and of the one that only hints at what is coming. */
    const val PulseCycle = "1.6s"

    /** The same breathing, a touch quicker, where the piece is smaller. */
    const val PulseCycleShort = "1.4s"

    /** A page arriving after a route change. */
    const val PageEnter = "260ms"

    /**
     * The same page leaving. Shorter than the entrance on purpose: waiting to be taken somewhere
     * is dead time, arriving somewhere is not.
     */
    const val PageLeave = "150ms"

    /**
     * A block appearing as it comes into view. Slower than anything in the application:
     * there it accompanies an action you just took, here it is the page introducing itself.
     */
    const val Reveal = "620ms"

    /** How long a floating piece takes to rise and come back. */
    const val FloatCycle = "7s"

    /** The breathing of the glow behind it, offset from the float so they do not beat. */
    const val GlowCycle = "4.5s"
}

/**
 * The bounds of the workspace split, in one place because three of them enforce it.
 *
 * The minimum used to be written twice - once in the divider, once in the stored preference -
 * and the stored one was only applied to the value computed on a first visit: a width already in
 * `localStorage` came back untouched, so a narrow split saved on one screen reopened as it was on
 * every other. Now the divider clamps, the preference clamps what it reads back, and the grid
 * track carries the floor itself, so not even a hand-edited value can collapse the column.
 */
object Layout {

    /**
     * The narrowest the chat may get, measured rather than chosen.
     *
     * The binding constraint is the composer's shortcut hints, and it is the Spanish ones that
     * bind: «Mayús+Enter salto de línea» needs **376 px** of panel before it stops fitting on
     * one line, against 332 for the English. The header is looser - even at its worst, with the
     * status and a two-digit version count taking 157 px, the title still keeps around
     * twenty-five characters here.
     *
     * The first value was 320, which is the width a phone screen suggests and has nothing to do
     * with what this panel actually holds: it left the hints stacked in Spanish at every width
     * the divider allowed. 450 read as too wide once the chat text dropped to `textSm`; 400 still
     * clears the measured floor, because the hints were already at `textXs` and did not shrink.
     */
    const val ChatMinWidth = 400

    /** However much the chat grows, the page keeps the rest of the window. */
    const val ChatMaxShare = 0.6

    /** How far one press of an arrow key moves the divider. */
    const val ChatWidthStep = 24
}

/**
 * The stacking order, which only exists so two things that overlap do not each pick a number.
 */
object Layer {

    /**
     * A panel that shares the width with the work on a phone and lies over it instead of
     * pushing it aside. Below a popover, which can open on top of this one.
     */
    const val Overlay = 50

    /** Anything that opens over the interface: a tooltip, a dropdown. */
    const val Popover = 60

    /** Above even those: the skip link, which has to clear the sticky bar it skips. */
    const val Skip = 70
}

/**
 * Line height by role. Written as a unitless number, which is what makes it inheritable
 * without compounding down the tree.
 */
object Leading {

    /**
     * Display, where two lines have to read as one block.
     *
     * Below 1 because the line box of a large size carries more air than the letters need;
     * at 1.2 the two lines of a headline drift apart into two headlines.
     */
    const val Display = "0.92"

    /** A heading, where the lines belong to one another. */
    const val Tight = "1.2"

    /** A short paragraph or a control's label. */
    const val Snug = "1.35"

    /** Running text. */
    const val Normal = "1.5"

    /** The conversation, which is read for a long stretch. */
    const val Relaxed = "1.6"
}

/**
 * Letter spacing, which only two roles need and both need it badly.
 *
 * It is set as a fraction of the size and not in pixels: tracking is a proportion of the
 * letter, so a value in pixels that looks right at 76px falls apart at 31px.
 */
object Tracking {

    /**
     * Display. A large size leaves gaps between letters that the eye reads as gaps between
     * words; pulling them in is what makes a headline look drawn rather than typed.
     */
    const val Display = "-0.045em"

    /** A heading below display size, where the same effect is milder. */
    const val Tight = "-0.015em"

    /**
     * A small uppercase label. The opposite correction: capitals at 12px collide, and
     * without air they read as one long word.
     */
    const val Eyebrow = "0.14em"

    /**
     * A two-letter code. The same correction as [Eyebrow] but a fraction of it: with two
     * capitals there is nothing to lose track of, only a pair that would otherwise touch.
     */
    const val Loose = "0.04em"
}

/**
 * The two families: the sans for the conversation, the mono for the artifact code, where
 * column alignment is information.
 *
 * They are system stacks: no network requests and no font swap on load.
 */
object Fonts {
    /**
     * Each family as a separate element: `Modifier.fontFamily` takes several names and joins
     * them itself. Passing the whole stack as one string compiles and leaves the page blank.
     */
    val Sans = arrayOf("system-ui", "-apple-system", "Segoe UI", "Roboto", "Helvetica Neue", "sans-serif")
    val Mono = arrayOf("ui-monospace", "SFMono-Regular", "SF Mono", "Menlo", "Consolas", "monospace")

    /**
     * The wordmark's family, and nothing else's. It is the one place worth a network request:
     * everywhere else reads at the system family and gains nothing from a second one.
     *
     * The stack behind it is [Sans], because a name set in Helvetica while the font arrives is
     * better than a name that is not there. It is loaded in the index of the site; declaring the
     * stack without loading the face leaves the page in the fallback and nothing says so.
     */
    val Display = arrayOf("Comfortaa") + Sans

    /** The same stacks, for when they go inside a hand-written CSS property. */
    val SansCss = Sans.joinToString(", ")
    val MonoCss = Mono.joinToString(", ")
    val DisplayCss = Display.joinToString(", ")
}

/**
 * The glass: translucent surfaces with the background blurred behind them.
 *
 * It is four pieces and removing any one spoils it: a semi-transparent fill,
 * `backdrop-filter` with blur and saturation (without it the color behind goes dull and the
 * glass looks grey), a one-pixel light edge, and an inner highlight on top. The fifth
 * condition is outside it: there has to be something behind to blur.
 *
 * Contrasts are measured over the composited color, not over the token. [Palette.Muted] drops
 * to 4.33:1 over [Raised], so bubbles and fields only use [Palette.Fg].
 */
object Glass {

    /** What rests inside a panel and stands out: the user bubbles. */
    const val Raised = "rgba(80, 149, 255, 0.16)"

    /** Separators inside a glass card: half the presence of the edge. */
    const val Divider = "1px solid rgba(231, 237, 247, 0.07)"

    /**
     * Blur and saturation. 24px: nothing is legible through it and the color still shows
     * where it comes from; below ~12px the background looks dirty instead of blurred.
     */
    const val Blur = "blur(24px) saturate(150%)"

    /**
     * The workspace panels' filter: the saturation of [Blur] without the blur.
     *
     * Behind them there is only the backdrop's gradients, hundreds of pixels wide, which a 24px
     * blur leaves the same: measured, the difference is under 1 level in 255. What it did cost
     * was every frame of every animation inside a panel, which re-ran the blur over the whole
     * panel: the preview's width change lost 8 frames of 43 with it and 2 without.
     */
    const val Tint = "saturate(150%)"

    /** What floats over a raised surface: the focus state of a control inside it. */
    const val Floating = "rgba(80, 149, 255, 0.26)"

}

/**
 * The icons Silk does not ship: the ones specific to this product. Silk's own (arrows,
 * `Check`, `Trash`, `Download`…) are used as they come.
 *
 * They are drawn as `mask-image` over `currentColor` and not as `<svg>`: building SVG with
 * the Compose HTML DSL requires `createElementNS`, which its tag builder does not use.
 * Whoever paints them must also add the `-webkit-` prefix, or Safari will not clip the mask.
 */
object Icons {

    private fun stroke(paths: String, width: String = "2"): String =
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' " +
            "fill='none' stroke='%23000' stroke-width='$width' stroke-linecap='round' " +
            "stroke-linejoin='round'%3E$paths%3C/svg%3E\")"

    private fun path(d: String) = "%3Cpath d='$d'/%3E"

    /** The product mark: a spark. */
    val Spark = stroke(
        path("M12 3v18") + path("M3 12h18") + path("M6.5 6.5l11 11") + path("M17.5 6.5l-11 11"),
        width = "1.6",
    )

    /** The conversation. */
    val Chat = stroke(path("M21 14a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z"))

    /** The session history. */
    val History = stroke(
        path("M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3 8.5") + path("M3 3.5V9h5.5") + path("M12 7.5V12l3 2"),
    )

    /** Ask for the same thing again. Silk ships no reload icon. */
    val Retry = stroke(path("M20.5 12a8.5 8.5 0 1 1-2.5-6") + path("M20.5 3.5V9H15"))

    /** The sections of the landing, on a screen too narrow to hold them in the bar. */
    val Menu = stroke(path("M4 7h16") + path("M4 12h16") + path("M4 17h16"))

    /** The same button once the sheet is open. */
    val Close = stroke(path("M6 6l12 12") + path("M18 6L6 18"))

    /**
     * Stop the turn: a filled square. Silk's `StopIcon` is a circle with a bar and in the
     * composer it reads as "pause".
     */
    const val Stop = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' " +
        "viewBox='0 0 24 24'%3E%3Crect x='6' y='6' width='12' height='12' rx='2' fill='%23000'/%3E%3C/svg%3E\")"

    /**
     * Attach a reference photo: a framed landscape with a plus in the corner the frame leaves
     * open, so it reads as adding a picture rather than as a picture.
     */
    val Photo = stroke(
        path("M12.5 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6.5") +
            path("M4 17l4.5-4.5a1.5 1.5 0 0 1 2 0L17 19") +
            path("M9 10a1.5 1.5 0 1 0 0-3a1.5 1.5 0 0 0 0 3z") +
            path("M18 3v6") + path("M15 6h6"),
        width = "1.8",
    )

    /** View at desktop width. */
    val Desktop = stroke(path("M3 4.5h18v11H3z") + path("M8.5 20h7") + path("M12 15.5V20"))

    /** View at phone width. */
    val Phone = stroke(path("M7.5 2.5h9v19h-9z") + path("M11 18.5h2"))

    /** Copying a message: one sheet on top of another. */
    val Copy = stroke(
        path("M9.5 8.5h9A1.5 1.5 0 0 1 20 10v9a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 8 19v-9a1.5 1.5 0 0 1 1.5-1.5z") +
            path("M16 8.5v-3A1.5 1.5 0 0 0 14.5 4h-9A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8"),
        width = "1.8",
    )

    /** It made it to the clipboard. */
    val Check = stroke(path("M5 12.5l4.5 4.5L19 7.5"))

    /** Taking a message out of the conversation. */
    val Trash = stroke(
        path("M4 7h16") + path("M9.5 7V4.5h5V7") +
            path("M6 7l1 12.5A1.5 1.5 0 0 0 8.5 21h7a1.5 1.5 0 0 0 1.5-1.5L18 7") +
            path("M10 11v6") + path("M14 11v6"),
        width = "1.8",
    )

    /** Writing it yourself: the blank session. */
    val Pen = stroke(path("M4 20h4L19 9l-4-4L4 16z") + path("M13.5 6.5l4 4"), width = "1.8")

    /** The agent thinking or planning: a light bulb. */
    val Idea = stroke(
        path("M9.5 18h5") + path("M10.5 21h3") +
            path("M12 3a6 6 0 0 0-3.6 10.8c.7.5 1.1 1.3 1.1 2.1V16h5v-.1c0-.8.4-1.6 1.1-2.1A6 6 0 0 0 12 3z"),
        width = "1.8",
    )

    /** Checking the page against the rules: a clipboard with a tick. */
    val Checklist = stroke(
        path("M9 3.5h6v3H9z") +
            path("M15 5h2.5A1.5 1.5 0 0 1 19 6.5v13a1.5 1.5 0 0 1-1.5 1.5h-11") +
            path("M6.5 21A1.5 1.5 0 0 1 5 19.5v-13A1.5 1.5 0 0 1 6.5 5H9") +
            path("M9 13.5l2 2 4-4"),
        width = "1.8",
    )

    /** Correcting what the checks found: a wrench. */
    val Wrench = stroke(
        path(
            "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.8-3.8a6 6 0 0 1-7.9 7.9l-6.9 6.9" +
                "a2.1 2.1 0 0 1-3-3l6.9-6.9a6 6 0 0 1 7.9-7.9z",
        ),
        width = "1.8",
    )

    /** Storing the page: a floppy disk, still the sign for saving. */
    val Save = stroke(
        path("M5 4h11l3 3v13H5z") + path("M8.5 4v4.5h6V4") + path("M8 20v-6h8v6"),
        width = "1.8",
    )

    /** Reading design instructions: an open book. */
    val Book = stroke(
        path("M12 6.5C10.5 5 8.5 4.5 4 4.5v14c4.5 0 6.5.5 8 2 1.5-1.5 3.5-2 8-2v-14c-4.5 0-6.5.5-8 2z") +
            path("M12 6.5v14"),
        width = "1.8",
    )

    /** Looking for photographs: a magnifying glass. */
    val Search = stroke(
        path("M10.5 17a6.5 6.5 0 1 0 0-13a6.5 6.5 0 0 0 0 13z") + path("M15.5 15.5L20 20"),
        width = "1.8",
    )

    /** More actions, stacked: the menu that holds the page's tools on a phone. */
    val More = stroke(path("M12 5h.01") + path("M12 12h.01") + path("M12 19h.01"), width = "3")

    /** The preview over the whole screen: four corners pushed outwards. */
    val Expand = stroke(path("M4 9V4h5") + path("M15 4h5v5") + path("M20 15v5h-5") + path("M9 20H4v-5"))

    /** Light mode. */
    val Sun = stroke(
        path("M12 4.5V2.5") + path("M12 21.5v-2") + path("M4.5 12h-2") + path("M21.5 12h-2") +
            path("M6.4 6.4L5 5") + path("M19 19l-1.4-1.4") + path("M17.6 6.4L19 5") +
            path("M5 19l1.4-1.4") + path("M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9z"),
        width = "1.8",
    )

    /** Dark mode. */
    val Moon = stroke(path("M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z"))

    /** Move on: the link out of a card, and the direction the page reads in. */
    val ArrowRight = stroke(path("M4 12h15") + path("M13 6l6 6-6 6"))

    /** Watching it happen: the second step, where the page is written in front of you. */
    val Eye = stroke(
        path("M2.5 12s3.8-6.5 9.5-6.5S21.5 12 21.5 12s-3.8 6.5-9.5 6.5S2.5 12 2.5 12z") +
            path("M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"),
        width = "1.7",
    )

    /** Taking it with you: the file coming down into your hands. */
    val Download = stroke(
        path("M12 3.5v10.5") + path("M7.5 10l4.5 4 4.5-4") + path("M4.5 16.5v2a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-2"),
        width = "1.7",
    )

    /** Leaving for another tab: what an example card does when it is opened. */
    val ArrowUpRight = stroke(path("M7.5 16.5l9-9") + path("M9 7.5h7.5V15"), width = "1.8")

    /** Turned over when its question opens: the only sign that a row can be pressed. */
    val ChevronDown = stroke(path("M6 9l6 6 6-6"))

    /** A shop with an awning: the business with a street door. */
    val Storefront = stroke(
        path("M3.5 9L5 4.5h14L20.5 9") +
            path("M5 9v10.5h14V9") +
            path("M3.5 9a2.6 2.6 0 0 0 5.3 0 2.6 2.6 0 0 0 5.3 0 2.6 2.6 0 0 0 5.4 0") +
            path("M9.75 19.5v-5.5h4.5v5.5"),
        width = "1.7",
    )

    /** A framed picture: work shown, which is what a portfolio is. */
    val Frame = stroke(
        path("M3.5 4.5h17v15h-17z") + path("M3.5 15.5l4.5-4 3.5 3 3-2.5 6 5") + path("M15.5 8.5h.01"),
        width = "1.7",
    )

    /** A date on a calendar. */
    val Calendar = stroke(
        path("M4 6.5h16v13H4z") + path("M4 10.5h16") + path("M8.5 3.5v4") + path("M15.5 3.5v4"),
        width = "1.7",
    )

    /**
     * Leaving: a door with the way out drawn through it.
     *
     * Not a key or a padlock, which would promise a lock that does not exist here: nothing is
     * closed by using it, the address is simply forgotten on this device.
     */
    val Exit = stroke(
        path("M14 4.5H6.75A1.75 1.75 0 0 0 5 6.25v11.5A1.75 1.75 0 0 0 6.75 19.5H14") +
            path("M11.5 12h9") + path("M17.5 8.5l3.5 3.5-3.5 3.5"),
    )

    /** Something taking off: the launch. */
    val Rocket = stroke(
        path("M12 3.2c3 2.4 4.4 5.6 4.4 9.3l-1.9 3.5h-5L7.6 12.5C7.6 8.8 9 5.6 12 3.2z") +
            path("M9.5 15.5l-2 4.5 3.4-1.6") +
            path("M14.5 15.5l2 4.5-3.4-1.6") +
            path("M12 9.5h.01"),
        width = "1.7",
    )
}
